import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from 'src/Contexts/ToastContext';
import { useUpdateInstallationMutation } from 'src/queries';
import type { Installation } from 'src/types';

import './ColorScaleControl.css';

interface ColorScaleControlProps {
  installation: Installation;
  // Which view's scale we're editing — picks the config key pair and is shown
  // in the header so the admin knows whether they're tuning voltage or deviation.
  mode: 'voltage' | 'deviation';
  // The domain currently in effect on the map (saved override or auto), used to
  // prefill the inputs so the admin tweaks from what they see.
  effectiveDomain: [number, number] | null;
  // Live preview while typing — Status forwards this to the map as colorOverride.
  onPreview: (domain: { min: number; max: number } | null) => void;
}

const KEYS = {
  voltage: ['color_scale_min', 'color_scale_max'],
  deviation: ['color_scale_dev_min', 'color_scale_dev_max'],
} as const;

const ColorScaleControl = ({
  installation,
  mode,
  effectiveDomain,
  onPreview,
}: ColorScaleControlProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const updateInstallation = useUpdateInstallationMutation();
  const [open, setOpen] = useState(false);

  const [minKey, maxKey] = KEYS[mode];
  const cfg = installation.configuration ?? {};
  const savedMin = cfg[minKey];
  const savedMax = cfg[maxKey];

  const [minStr, setMinStr] = useState('');
  const [maxStr, setMaxStr] = useState('');

  // Prefill from saved config, else the current auto domain, whenever the panel
  // opens or the mode/installation changes.
  useEffect(() => {
    if (!open) return;
    const initialMin = typeof savedMin === 'number' ? savedMin : effectiveDomain?.[0];
    const initialMax = typeof savedMax === 'number' ? savedMax : effectiveDomain?.[1];
    setMinStr(initialMin != null ? String(round(initialMin)) : '');
    setMaxStr(initialMax != null ? String(round(initialMax)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, installation.id]);

  const parsed = (): { min: number; max: number } | null => {
    const min = parseFloat(minStr);
    const max = parseFloat(maxStr);
    if (Number.isNaN(min) || Number.isNaN(max) || min >= max) return null;
    return { min, max };
  };

  const handlePreview = () => {
    const d = parsed();
    if (!d) {
      toast.error(t('colorScale.invalid'));
      return;
    }
    onPreview(d);
  };

  const handleSave = () => {
    const d = parsed();
    if (!d) {
      toast.error(t('colorScale.invalid'));
      return;
    }
    updateInstallation.mutate(
      {
        id: installation.id,
        data: {
          configuration: { ...cfg, [minKey]: d.min, [maxKey]: d.max },
        } as Partial<Installation>,
      },
      {
        onSuccess: () => {
          toast.success(t('colorScale.saved'));
          onPreview(null); // saved value now flows from config; drop the preview
        },
      }
    );
  };

  const handleReset = () => {
    // Clear the saved override for this mode -> map reverts to auto domain.
    const next = { ...cfg };
    delete next[minKey];
    delete next[maxKey];
    updateInstallation.mutate(
      { id: installation.id, data: { configuration: next } as Partial<Installation> },
      {
        onSuccess: () => {
          toast.success(t('colorScale.reset'));
          onPreview(null);
          setOpen(false);
        },
      }
    );
  };

  if (!open) {
    return (
      <button type="button" className="colorscale-toggle" onClick={() => setOpen(true)}>
        {t('colorScale.edit', { mode: t(`status.view.${mode}`) })}
      </button>
    );
  }

  return (
    <div className="colorscale-panel" role="group" aria-label={t('colorScale.title')}>
      <span className="colorscale-panel-title">
        {t('colorScale.title')} — {t(`status.view.${mode}`)}
      </span>
      <label className="colorscale-field">
        {t('colorScale.min')}
        <input
          type="number"
          step="any"
          value={minStr}
          onChange={(e) => setMinStr(e.target.value)}
        />
      </label>
      <label className="colorscale-field">
        {t('colorScale.max')}
        <input
          type="number"
          step="any"
          value={maxStr}
          onChange={(e) => setMaxStr(e.target.value)}
        />
      </label>
      <button type="button" className="colorscale-btn" onClick={handlePreview}>
        {t('colorScale.preview')}
      </button>
      <button
        type="button"
        className="colorscale-btn colorscale-btn--primary"
        onClick={handleSave}
        disabled={updateInstallation.isPending}
      >
        {t('colorScale.save')}
      </button>
      <button type="button" className="colorscale-btn" onClick={handleReset}>
        {t('colorScale.auto')}
      </button>
      <button
        type="button"
        className="colorscale-btn"
        onClick={() => {
          onPreview(null);
          setOpen(false);
        }}
      >
        {t('colorScale.close')}
      </button>
    </div>
  );
};

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

export default ColorScaleControl;
